import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { user as getUser } from "localStorage";
import { useTranslation } from "react-i18next";
import { Box, Pagination, IconButton, Tooltip, useTheme, useMediaQuery } from "@mui/material";
import { FileDownloadOutlined, TuneOutlined, AddCardOutlined } from "@mui/icons-material";
import Swal from "sweetalert2";
import { getWalletTransactions, getBranchCashTransactions } from "api/financial";
import TransactionDetailDialog from "./TransactionDetailDialog";
import { formatWalletTxnDetail } from "utils/formatWalletTxnDetail";
import AdjustBalanceDialog from "components/AdjustBalanceDialog";
import BranchCashDepositDialog from "components/BranchCashDepositDialog";
import DailySummary from "./DailySummary";
import StationSwitcher from "components/StationSwitcher";
import { useSetBreadcrumb } from "hooks/BreadcrumbContext";

const FONT = "'Plus Jakarta Sans', 'Noto Sans Lao', system-ui, sans-serif";
const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const SYM = { THB: "฿", LAK: "₭", USD: "$" };
const money = (n, c) => `${SYM[c] || ""}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtD = (d) => (d ? new Date(d).toLocaleDateString() : "");
const fmtT = (d) => (d ? new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "");
const initials = (s) => (s || "?").slice(0, 2).toUpperCase();
const WALLET_TYPES = ["all", "deposit", "withdraw", "payment", "purchase", "exchange", "adjustment"];
const ARROW_OUT = "M6 10V2M2 6l4-4 4 4";
const ARROW_IN = "M6 2v8M2 6l4 4 4-4";

function typeStyle(purpose, txnType) {
  if (txnType === "debit") return { bg: "#fff1f2", border: "#fca5a5", col: "#c0343a", arrow: ARROW_OUT };
  if (purpose === "exchange") return { bg: "#f0f9ff", border: "#7dd3fc", col: "#0369a1", arrow: ARROW_IN };
  return { bg: "#f0fdf4", border: "#86efac", col: "#15803d", arrow: ARROW_IN };
}

const labelSx = { fontSize: "10.5px", letterSpacing: ".14em", color: "#a8a3b4", fontWeight: 700 };
const headCell = { fontSize: 11, fontWeight: 700, letterSpacing: ".1em", color: "#a8a3b4", textTransform: "uppercase" };
const GRID = "1.6fr 1.3fr 1.1fr 1.2fr 2fr 36px";

function toCsv(rows, isBranch) {
  const head = ["Date", "Type", "Amount", "Currency", "Balance after", ...(isBranch ? ["Customer"] : []), "Reference", "ID"];
  const lines = rows.map((r) => [
    new Date(r.createdAt).toISOString(), r.purpose, r.amount, r.currency, r.balanceAfter,
    ...(isBranch ? [r.customer?.name || ""] : []), r.reference || "", r.id,
  ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
  return [head.join(","), ...lines].join("\n");
}

export default function FinancialTransactions() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { accountId, stationId } = useParams();
  const isBranch = !!stationId;

  // Station switcher: only the stations assigned to the logged-in user. If the
  // current station isn't among them (e.g. opened by link), keep it selectable.
  const myStations = getUser()?.stations || [];
  const stationOptions = myStations.some((s) => s.id === stationId)
    ? myStations
    : [{ id: stationId, name: stationId }, ...myStations];
  const currentStationName = isBranch ? (stationOptions.find((s) => s.id === stationId)?.name || stationId) : "";

  const [rows, setRows] = useState([]);
  const [account, setAccount] = useState(null);
  const [summary, setSummary] = useState({ count: 0, in: {}, out: {} });
  const [meta, setMeta] = useState({ pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [type, setType] = useState("all"); // wallet: purpose filter
  const [exporting, setExporting] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  // Bumped on every recorded movement so DailySummary (a sibling, not a child
  // of this page's own `load`) refetches its feed too — otherwise a just-recorded
  // adjustment/deposit only updates this page's own summary state.
  const [refreshToken, setRefreshToken] = useState(0);

  // Branch's own date/customer filtering now lives in DailySummary — this fetch is
  // only for the all-time summary (transaction count/in/out/net for wallet mode;
  // adjust/deposit balances for branch mode), which the backend computes
  // unfiltered regardless, so a small unfiltered page is all that's needed here.
  const fetchPage = useCallback((p, limit) => {
    if (isBranch) return getBranchCashTransactions(stationId, { page: p, limit });
    return getWalletTransactions(accountId, { page: p, limit, dateFrom, dateTo, purpose: type === "all" ? undefined : type });
  }, [isBranch, stationId, accountId, dateFrom, dateTo, type]);

  const load = useCallback(async () => {
    const res = await fetchPage(page, 20);
    setRows(res && res.success ? res.data : []);
    setMeta((res && res.pagination) || { pages: 1, total: 0 });
    setSummary((res && res.summary) || { count: 0, in: {}, out: {} });
    if (!isBranch) setAccount((res && res.account) || null);
  }, [fetchPage, page, isBranch]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    const res = await fetchPage(1, 10000);
    const all = res && res.success ? res.data : [];
    const blob = new Blob([toCsv(all, isBranch)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `transactions-${isBranch ? stationId : accountId}-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    setExporting(false);
  };

  const onAdjustSuccess = () => {
    setAdjustOpen(false);
    load();
    setRefreshToken((n) => n + 1);
    Swal.fire({ icon: "success", text: t("financial.adjustRecorded"), toast: true, position: "top-right", timer: 2200, showConfirmButton: false });
  };

  const onDepositSuccess = () => {
    setDepositOpen(false);
    load();
    setRefreshToken((n) => n + 1);
    Swal.fire({ icon: "success", text: t("branchCash.depositRecorded"), toast: true, position: "top-right", timer: 2200, showConfirmButton: false });
  };

  const name = isBranch ? stationId : (account?.customer?.name || "");
  const phone = isBranch ? "" : (account?.customer?.whatsapp || "");

  useSetBreadcrumb([
    { label: t("financial.title"), to: "/financial" },
    ...(isBranch ? [] : [{ label: t("financial.allWallets"), to: "/financial/wallets" }]),
    { label: isBranch ? currentStationName : name },
  ]);

  const inTHB = (summary.in && summary.in.THB) || 0;
  const outTHB = (summary.out && summary.out.THB) || 0;
  const netTHB = isBranch ? inTHB - outTHB : (account ? account.THB || 0 : inTHB - outTHB);

  // Branch drawer balance per currency = all-time cash in − cash out — feeds
  // AdjustBalanceDialog/BranchCashDepositDialog below (their own THB/LAK/USD KPI
  // display now lives solely in DailySummary's "TODAY" row).
  const sIn = summary.in || {};
  const sOut = summary.out || {};
  const balTHB = (sIn.THB || 0) - (sOut.THB || 0);
  const balLAK = (sIn.LAK || 0) - (sOut.LAK || 0);
  const balUSD = (sIn.USD || 0) - (sOut.USD || 0);

  // Current per-currency balance feeding the adjust dialog: wallet → the account doc;
  // branch → all-time cash-in minus cash-out (same maths as the drawer KPIs above).
  const adjustBalances = isBranch
    ? { THB: balTHB, LAK: balLAK, USD: balUSD }
    : { THB: (account && account.THB) || 0, LAK: (account && account.LAK) || 0, USD: (account && account.USD) || 0 };
  const adjustTarget = isBranch
    ? { id: stationId, name: currentStationName }
    : { id: account && account.id, name: (account && account.customer && account.customer.name) || "", phone };
  const canAdjust = isBranch ? !!stationId : !!(account && account.id);

  return (
    <Box sx={{ fontFamily: FONT, maxWidth: 1280, mr: "auto", p: { xs: "20px", md: "28px 40px 60px" } }}>
      {/* header: customer/station + export */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "14px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "14px", background: "linear-gradient(135deg,#7c3aed,#4c1d95)", color: "#fff", fontFamily: MONO, fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{initials(isBranch ? currentStationName : name)}</Box>
          {isBranch ? (
            <StationSwitcher
              size="lg"
              value={stationId}
              options={stationOptions.map((s) => ({ id: s.id, name: s.name || s.id }))}
              onChange={(id) => navigate(`/financial/branch-cash/${encodeURIComponent(id)}`)}
            />
          ) : (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <Box component="h1" sx={{ m: 0, fontSize: 22, fontWeight: 800, color: "#16131f", letterSpacing: "-.01em" }}>{name}</Box>
                {phone && <Box component="span" sx={{ fontSize: 14, fontWeight: 500, color: "#9b96a6", fontFamily: MONO }}>· {phone}</Box>}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "5px", flexWrap: "wrap" }}>
                <Box sx={{ fontSize: "11.5px", fontWeight: 700, color: "#6d28d9", background: "#f3f0fc", border: "1px solid #ddd0fa", padding: "3px 10px", borderRadius: "999px" }}>{summary.count} {t("financial.transactions")}</Box>
                <Box sx={{ fontSize: "11.5px", fontWeight: 600, color: netTHB >= 0 ? "#15a36a" : "#c0343a", background: netTHB >= 0 ? "#e7f8ee" : "#fdeef0", padding: "3px 10px", borderRadius: "999px" }}>{money(netTHB, "THB")} {t("financial.balance")}</Box>
              </Box>
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Deposit to bank (branch only) — placed before Adjust */}
          {isBranch && (
            isMobile ? (
              <Tooltip title={t("branchCash.depositToBank")}><span><IconButton onClick={() => setDepositOpen(true)} disabled={!stationId} sx={{ color: "#6d28d9", border: "1.5px solid #ddd0fa", borderRadius: "11px" }}><AddCardOutlined /></IconButton></span></Tooltip>
            ) : (
              <Box component="button" onClick={() => setDepositOpen(true)} disabled={!stationId}
                sx={{ display: "inline-flex", alignItems: "center", gap: "8px", fontFamily: FONT, fontSize: 13, fontWeight: 700, color: "#5b21b6", background: "#fff", border: "1.5px solid #ddd0fa", borderRadius: "11px", padding: "10px 18px", cursor: stationId ? "pointer" : "not-allowed", opacity: stationId ? 1 : 0.5, boxShadow: "0 1px 2px rgba(20,18,30,.04)", "&:hover": { background: "#f3f0fc" } }}>
                <AddCardOutlined sx={{ fontSize: 16, color: "#6d28d9" }} />{t("branchCash.depositToBank")}
              </Box>
            )
          )}
          {/* Adjust balance — placed before Export CSV */}
          {isMobile ? (
            <Tooltip title={t("financial.adjust")}><span><IconButton onClick={() => setAdjustOpen(true)} disabled={!canAdjust} sx={{ color: "#6d28d9", border: "1.5px solid #ddd0fa", borderRadius: "11px" }}><TuneOutlined /></IconButton></span></Tooltip>
          ) : (
            <Box component="button" onClick={() => setAdjustOpen(true)} disabled={!canAdjust}
              sx={{ display: "inline-flex", alignItems: "center", gap: "8px", fontFamily: FONT, fontSize: 13, fontWeight: 700, color: "#5b21b6", background: "#fff", border: "1.5px solid #ddd0fa", borderRadius: "11px", padding: "10px 18px", cursor: canAdjust ? "pointer" : "not-allowed", opacity: canAdjust ? 1 : 0.5, boxShadow: "0 1px 2px rgba(20,18,30,.04)", "&:hover": { background: "#f3f0fc" } }}>
              <TuneOutlined sx={{ fontSize: 16, color: "#6d28d9" }} />{t("financial.adjust")}
            </Box>
          )}

          {/* Export CSV (wallet only — branch's ledger is now searched/browsed via
              the DailySummary feed below, not exported as a flat file) */}
          {!isBranch && (
            isMobile ? (
              <Tooltip title={t("financial.exportCsv")}><span><IconButton onClick={exportCsv} disabled={exporting} sx={{ color: "#6d28d9", border: "1.5px solid #ddd0fa", borderRadius: "11px" }}><FileDownloadOutlined /></IconButton></span></Tooltip>
            ) : (
              <Box component="button" onClick={exportCsv} disabled={exporting}
                sx={{ display: "inline-flex", alignItems: "center", gap: "8px", fontFamily: FONT, fontSize: 13, fontWeight: 700, color: "#5b21b6", background: "#fff", border: "1.5px solid #ddd0fa", borderRadius: "11px", padding: "10px 18px", cursor: "pointer", boxShadow: "0 1px 2px rgba(20,18,30,.04)", "&:hover": { background: "#f3f0fc" } }}>
                <FileDownloadOutlined sx={{ fontSize: 16, color: "#6d28d9" }} />{t("financial.exportCsv")}
              </Box>
            )
          )}
        </Box>
      </Box>

      {/* KPI strip — branch mode's own top-level tiles were dropped (DailySummary's
          "TODAY" KPI row below already covers this station's current balances, see
          also scenes/branchCash/station.jsx); wallet mode keeps its all-time
          transactions/in/out/net summary, which has nothing else showing it. */}
      {!isBranch && (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,1fr)" }, gap: "16px", mt: "22px" }}>
        <Box sx={{ background: "linear-gradient(155deg,#fff 0%,#f5efff 100%)", border: "1px solid #ece4fb", borderRadius: "18px", p: "18px 20px", boxShadow: "0 1px 2px rgba(20,18,30,.04),0 14px 28px -20px rgba(86,40,160,.22)" }}>
          <Box sx={labelSx}>{t("financial.totalTransactions").toUpperCase()}</Box>
          <Box sx={{ fontFamily: MONO, fontSize: 28, fontWeight: 700, color: "#161420", mt: "8px" }}>{summary.count}</Box>
        </Box>
        <Kpi label={t("financial.totalIn")} value={`+${money(inTHB, "THB")}`} color="#117a4f" />
        <Kpi label={t("financial.totalOut")} value={`-${money(outTHB, "THB")}`} color="#c0343a" />
        <Kpi label={t("financial.netBalance")} value={money(netTHB, "THB")} color="#161420" />
      </Box>
      )}

      {/* today: customer visits + cash movements, click any row for detail */}
      {isBranch && (
        <Box sx={{ mt: "28px" }}>
          <DailySummary stationId={stationId} mode="financial" refreshToken={refreshToken} />
        </Box>
      )}

      {/* filter bar (wallet only — branch's list/filter now lives in DailySummary above) */}
      {!isBranch && (
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mt: "22px", flexWrap: "wrap" }}>
        <DateBox label={t("financial.dateFrom")} value={dateFrom} onChange={(v) => { setPage(1); setDateFrom(v); }} />
        <DateBox label={t("financial.dateTo")} value={dateTo} onChange={(v) => { setPage(1); setDateTo(v); }} />
        <Box sx={{ display: "flex", gap: "4px", background: "#fff", border: "1px solid #ece9f1", p: "4px", borderRadius: "11px", boxShadow: "0 1px 2px rgba(20,18,30,.04)", flexWrap: "wrap" }}>
          {WALLET_TYPES.map((tp) => {
            const active = type === tp;
            return (
              <Box component="button" key={tp} onClick={() => { setPage(1); setType(tp); }}
                sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, padding: "6px 13px", borderRadius: "8px", border: "none", cursor: "pointer", background: active ? "#6d28d9" : "transparent", color: active ? "#fff" : "#7c7787", textTransform: "capitalize" }}>{tp === "all" ? t("financial.all") : tp}</Box>
            );
          })}
        </Box>
        <Box sx={{ ml: "auto", fontSize: "12.5px", color: "#a8a3b4", fontWeight: 600 }}>{meta.total || 0} {t("financial.transactions")}</Box>
      </Box>
      )}

      {/* list — cards on mobile, table on desktop (wallet only) */}
      {!isBranch && (rows.length === 0 ? (
        <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "22px", mt: "16px", p: "50px 24px", textAlign: "center", color: "#b9b4c4" }}>{t("financial.noData")}</Box>
      ) : isMobile ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", mt: "16px" }}>
          {rows.map((r) => {
            const s = typeStyle(r.purpose, r.txn_type);
            const out = r.txn_type === "debit";
            return (
              <Box key={r.id} onClick={() => setDetailId(r.id)}
                sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "16px", p: "14px 16px", cursor: "pointer", boxShadow: "0 1px 2px rgba(20,18,30,.04)" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: 12, fontWeight: 700, padding: "5px 11px", borderRadius: "999px", border: `1.5px solid ${s.border}`, background: s.bg, color: s.col, textTransform: "capitalize" }}>
                    <Box component="svg" width="11" height="11" viewBox="0 0 12 12" fill="none"><path d={s.arrow} stroke={s.col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></Box>
                    {r.purpose}
                  </Box>
                  <Box sx={{ fontFamily: MONO, fontSize: 17, fontWeight: 700, color: out ? "#c0343a" : "#117a4f" }}>{out ? "-" : "+"}{money(r.amount, r.currency)}</Box>
                </Box>
                <Box sx={{ fontSize: 12, color: "#9b96a6", mt: "8px" }}>
                  {fmtD(r.createdAt)} · {fmtT(r.createdAt)}{isBranch && r.customer?.name ? ` · ${r.customer.name}` : ""}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", mt: "6px" }}>
                  <Box sx={{ fontSize: 12, color: "#6b6775" }}>{t("financial.balanceAfter")}: <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, color: "#2c2937" }}>{money(r.balanceAfter, r.currency)}</Box></Box>
                  <Box sx={{ fontFamily: MONO, fontSize: "11px", color: "#b3aebd", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "45%" }}>{formatWalletTxnDetail(r, t) || r.reference || ""}</Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "22px", overflow: "hidden", mt: "16px", boxShadow: "0 1px 2px rgba(20,18,30,.04),0 16px 40px -28px rgba(20,18,30,.18)" }}>
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: 720 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: GRID, alignItems: "center", p: "13px 24px", background: "#faf9fc", borderBottom: "1.5px solid #ece9f1" }}>
                <Box sx={headCell}>{t("financial.date")}</Box>
                <Box sx={headCell}>{t("financial.type")}</Box>
                <Box sx={{ ...headCell, color: "#6d28d9", textAlign: "right" }}>{t("financial.amount")}</Box>
                <Box sx={{ ...headCell, textAlign: "right" }}>{t("financial.balanceAfter")}</Box>
                <Box sx={{ ...headCell, pl: "24px" }}>{t("financial.description")}</Box>
                <Box />
              </Box>
              {rows.map((r) => {
                const s = typeStyle(r.purpose, r.txn_type);
                const out = r.txn_type === "debit";
                return (
                  <Box key={r.id} onClick={() => setDetailId(r.id)}
                    sx={{ display: "grid", gridTemplateColumns: GRID, alignItems: "center", p: "0 24px", borderTop: "1px solid #f4f2f8", minHeight: 58, cursor: "pointer", "&:hover": { background: "#faf9fc" } }}>
                    <Box sx={{ py: "14px" }}>
                      <Box sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 500, color: "#2c2937" }}>{fmtD(r.createdAt)}</Box>
                      <Box sx={{ fontSize: 11, color: "#b3aebd", mt: "2px" }}>{fmtT(r.createdAt)}{isBranch && r.customer?.name ? ` · ${r.customer.name}` : ""}</Box>
                    </Box>
                    <Box>
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: 12, fontWeight: 700, padding: "5px 11px", borderRadius: "999px", border: `1.5px solid ${s.border}`, background: s.bg, color: s.col, textTransform: "capitalize" }}>
                        <Box component="svg" width="11" height="11" viewBox="0 0 12 12" fill="none"><path d={s.arrow} stroke={s.col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></Box>
                        {r.purpose}
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: "right", fontFamily: MONO, fontSize: 15, fontWeight: 700, color: out ? "#c0343a" : "#117a4f" }}>{out ? "-" : "+"}{money(r.amount, r.currency)}</Box>
                    <Box sx={{ textAlign: "right", fontFamily: MONO, fontSize: 14, fontWeight: 600, color: "#2c2937" }}>{money(r.balanceAfter, r.currency)}</Box>
                    <Box sx={{ minWidth: 0, pl: "24px", pr: "12px" }}>
                      <Box sx={{ fontFamily: MONO, fontSize: "11.5px", color: "#b3aebd", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{formatWalletTxnDetail(r, t) || r.reference || "—"}</Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Box sx={{ width: 26, height: 26, borderRadius: "50%", border: "1.5px solid #ddd6fe", display: "flex", alignItems: "center", justifyContent: "center", "&:hover": { background: "#f3f0fc" } }}>
                        <Box component="svg" width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="#8b5cf6" strokeWidth="1.5" /><path d="M8 7v4M8 5.5v.5" stroke="#8b5cf6" strokeWidth="1.5" strokeLinecap="round" /></Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      ))}

      {!isBranch && meta.pages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
          <Pagination color="secondary" count={meta.pages} page={page} onChange={(e, v) => setPage(v)} />
        </Box>
      )}

      <TransactionDetailDialog open={!!detailId} txId={detailId} onClose={() => setDetailId(null)} />
      <AdjustBalanceDialog
        mode={isBranch ? "branch" : "wallet"}
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        onSuccess={onAdjustSuccess}
        balances={adjustBalances}
        target={adjustTarget}
      />
      {isBranch && (
        <BranchCashDepositDialog
          mode="financial"
          open={depositOpen}
          onClose={() => setDepositOpen(false)}
          onSuccess={onDepositSuccess}
          stations={stationId ? [{ stationId, stationName: currentStationName, THB: balTHB, LAK: balLAK, USD: balUSD }] : []}
        />
      )}
    </Box>
  );
}

function Kpi({ label, value, color, sub, highlight }) {
  return (
    <Box sx={{
      background: highlight ? "linear-gradient(155deg,#fff 0%,#f5efff 100%)" : "#fff",
      border: `1px solid ${highlight ? "#ece4fb" : "#ece9f1"}`,
      borderRadius: "18px",
      p: "18px 20px",
      boxShadow: highlight
        ? "0 1px 2px rgba(20,18,30,.04),0 14px 28px -20px rgba(86,40,160,.22)"
        : "0 1px 2px rgba(20,18,30,.04),0 14px 28px -20px rgba(20,18,30,.12)",
    }}>
      <Box sx={labelSx}>{label.toUpperCase()}</Box>
      <Box sx={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, color, mt: "8px" }}>{value}</Box>
      {sub && <Box sx={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: "#a8a3b4", mt: "3px" }}>{sub}</Box>}
    </Box>
  );
}

function DateBox({ label, value, onChange }) {
  return (
    <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "11px", px: "14px", height: 42, display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 1px 2px rgba(20,18,30,.04)" }}>
      <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: "#a8a3b4", letterSpacing: ".08em", whiteSpace: "nowrap" }}>{label.toUpperCase()}</Box>
      <Box component="input" type="date" value={value} onChange={(e) => onChange(e.target.value)}
        sx={{ fontFamily: MONO, fontSize: 13, color: "#2c2937", border: "none", outline: "none", background: "transparent", cursor: "pointer" }} />
    </Box>
  );
}
